import axios, { AxiosRequestConfig } from 'axios';

import { API_BASE_URL } from '../constant';
import { languageHeaders } from '../requestLanguage';

/** ADM-17。管理画面のセッションの経路（サーバー: api/web_session.py）。 */
export const LOGIN_ENDPOINT = '/v1/web/token';

/** Cookie を使う経路（そのログインの sid の下）。 */
export type WebSessionAction = 'refresh' | 'logout' | 'support/enter' | 'support/return';

/**
 * EV-491（REVIEW-1027-01）。Cookie を使う経路は、ログイン（sid）ごと（/v1/web/sessions/<sid>/...）。
 *
 * サーバーは更新用トークンの Cookie の Path をログインごと（/v1/web/sessions/<sid>/）にする。ブラウザは名前と Path の
 * 組で Cookie を持つので、離れた画面の更新（keepalive）の応答がいつ届いても、前のセッションの Path の Cookie しか
 * 書き換えられない（新しいログインの Cookie を上書きできない）。経路の sid は、このタブのセッションの印
 * （「sid:user_id」）の sid。サーバーは、経路の sid と Cookie のログインが違えば 409 session_mismatch で断る。
 */
export function webSessionPath(sid: string, action: WebSessionAction): string {
  return `/v1/web/sessions/${encodeURIComponent(sid)}/${action}`;
}

/**
 * ADM-17。Cookie を使う要求に付ける CSRF 用のヘッダ。サーバーはこのヘッダと
 * 許可した Origin の両方が無ければ拒否する。別の Origin の画面がこのヘッダを
 * 付けるには CORS の事前確認が要り、許可していない Origin は通らない。
 */
export const CSRF_HEADERS = { 'X-Requested-By': 'yometel-admin' };

/** 更新の時間設定（テストで短くできるよう、まとめておく）。 */
export const refreshTiming = {
  /** 更新の応答を待つ上限。超えたらロックを手放し、再試行できるエラーにする。 */
  timeoutMs: 15000,
  /** EV-462。前の画面が更新の応答の前に離れていたとき、401 の後に取り直すまでの待ち時間と回数。 */
  interruptedRetryDelayMs: 1000,
  interruptedRetries: 2,
};

/**
 * 管理画面のセッションの経路へ、資格情報（Cookie）と CSRF 用のヘッダを付けて送る。
 * YAC-2026-009 PR7b。運営のサポートの「入る」は運営の access も要るので、ヘッダを足せるようにした（components/Support）。
 */
export function postWebSession(
  path: string,
  body: Record<string, unknown> | null = null,
  keepalive = false,
  headers: Record<string, string> = {}
) {
  return axios.post(`${API_BASE_URL}${path}`, body, {
    withCredentials: true,
    // EV-271。表示言語も送る（エラー文の言語をそろえる）。
    headers: { ...CSRF_HEADERS, ...languageHeaders(), ...headers },
    timeout: refreshTiming.timeoutMs,
    // EV-462。更新は keepalive の fetch で送る（画面を離れても切らない。回転した Cookie を失わない）。
    // fetch が無い環境では今までと同じ XHR。axios 1.7.2 の fetch は withCredentials を credentials に
    // 移さないので、credentials を直に指定する。
    ...(keepalive
      ? { adapter: ['fetch', 'xhr'], fetchOptions: { keepalive: true, credentials: 'include' } }
      : {}),
  } as AxiosRequestConfig);
}

/** セッションの識別子（「sid:user_id」）の sid。 */
export function sidOf(identity: string | null): string | null {
  if (!identity) return null;
  const sid = identity.slice(0, identity.indexOf(':'));
  return sid || null;
}
